import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  SocialAuthServiceConfig,
  GoogleLoginProvider,
  SOCIAL_AUTH_CONFIG,
} from '@abacritt/angularx-social-login';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withHashLocation,
  withComponentInputBinding,
  withRouterConfig,
  Router,
  withPreloading,
} from '@angular/router';
import * as Sentry from '@sentry/angular';
import { routes } from './app.routes';
import { authInterceptor } from '@core/interceptors/auth.interceptor';
import { loadingInterceptor } from '@core/interceptors/loading.interceptor';
import { RefreshAuthService } from '@core/services/auth/refresh-auth.service';
import { provideApollo } from '@core/providers/apollo';
import { FeatureFlagService } from './core/services/feature-flag/feature-flag.service';
import { RealtimeGlobalEventListenerService } from '@core/services/realtime/realtime-global-event-listener.service';
import { provideNzI18n, pt_BR } from 'ng-zorro-antd/i18n';
import { FluxPreloadStrategy } from '@core/router/preloading/flux-preload.strategy';
import { provideTabSync } from '@core/providers/tab-sync.provider';
import { fluxLogger } from '@core/services/logger/logger.instance';
import { BlogNotificationService } from '@core/services/blog-notification/blog-notification.service';
import { IaraCreditsService } from './features/iara-credits/services/iara-credits.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(
      routes,
      withHashLocation(),
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withPreloading(FluxPreloadStrategy),
    ),
    provideHttpClient(withInterceptors([authInterceptor, loadingInterceptor])),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideAnimationsAsync(),
    provideApollo(),
    {
      provide: SOCIAL_AUTH_CONFIG,
      useValue: {
        autoLogin: false,
        lang: 'pt',
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider(
              '508717818542-s5m9fdbsi71kicmssor6fd2ojeijki8p.apps.googleusercontent.com',
            ),
          },
        ],
        onError: (err) => {
          fluxLogger.extend('social-auth').error(err);
          Sentry.captureException(err, {
            tags: {
              module: 'SocialAuthServiceConfig',
            },
          });
        },
      } as SocialAuthServiceConfig,
    },
    provideNzI18n(pt_BR),
    RefreshAuthService.provider(),
    FeatureFlagService.provider(),
    RealtimeGlobalEventListenerService.provider(),
    provideTabSync(),
    BlogNotificationService.provider(),
    IaraCreditsService.provider(),
  ],
};
